import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { reviewService } from '../services/reviewService';
import { ReviewsQueryParams } from '../types/review';

export const REVIEWS_QUERY_KEY = 'reviews';

/**
 * Fetches a paginated list of reviews filtered by status and/or apartmentId.
 * Query key includes all filter + pagination params so each unique combination
 * is cached independently.
 */
export const useReviews = (
  params: ReviewsQueryParams = {},
  page = 0,
  size = 20,
) =>
  useQuery({
    queryKey: [REVIEWS_QUERY_KEY, { ...params, page, size }] as const,
    queryFn: () => reviewService.getReviews(params, { page, size }),
  });

/** Mutation: approve (publish) a review. Invalidates the full reviews cache on success. */
export const useApproveReview = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => reviewService.approveReview(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [REVIEWS_QUERY_KEY] });
    },
  });
};

/** Mutation: reject a review. Invalidates the full reviews cache on success. */
export const useRejectReview = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => reviewService.rejectReview(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [REVIEWS_QUERY_KEY] });
    },
  });
};
